import { useState, useCallback } from "react";
import { useImageUpload } from "./useImageUpload";
import getCroppedImg from "../pages/users/cropimage";
import { dataURLtoFile } from "../helpers/utils";
import { FileValidationConfig } from "../utils/fileValidation";

export const useImageCrop = (config?: FileValidationConfig) => {
  const { imageToUpload, imagePreview, handleImageUpload, setImageToUpload } =
    useImageUpload(config);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  const onCropComplete = useCallback(
    (_croppedArea: any, pixels: any) => {
      setCroppedAreaPixels(pixels);
    },
    []
  );

  const reset = useCallback(() => {
    setImageToUpload(null);
    setCroppedAreaPixels(null);
    setRotation(0);
    setZoom(1);
  }, [setImageToUpload]);

  const getCroppedFile = useCallback(
    async (filename?: string): Promise<File | null> => {
      if (!imageToUpload || !croppedAreaPixels) return null;
      const blobUrl = URL.createObjectURL(
        new Blob([imageToUpload], { type: "image" })
      );
      try {
        const croppedImage = await getCroppedImg(
          blobUrl,
          croppedAreaPixels,
          rotation
        );
        if (!croppedImage) return null;
        return dataURLtoFile(croppedImage, filename || imageToUpload.name);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    },
    [imageToUpload, croppedAreaPixels, rotation]
  );

  return {
    imageToUpload,
    imagePreview,
    handleImageUpload,
    setImageToUpload,
    croppedAreaPixels,
    rotation,
    setRotation,
    zoom,
    setZoom,
    onCropComplete,
    reset,
    getCroppedFile,
  };
};

export type ImageCropState = ReturnType<typeof useImageCrop>;
